import React, { useCallback, useRef, useState } from 'react';

import { Box } from 'ml-rubiks-styleguide/src/box';
import { Button } from 'ml-rubiks-styleguide/src/button';
import { IconChevronRight } from 'ml-rubiks-styleguide/src/icon';
import {
    Signature,
    SignatureFieldData
} from 'ml-rubiks-styleguide/src/signature';

import { useFeatureFlags } from '../../contexts/feature-flags-context';
import { useI18n } from '../../contexts/i18n-context';
import { useContainerWidth } from '../../hooks/use-container-width';
import { SIGNATURE_MAX_WIDTH } from '../../providers/direct-debit/constants';

interface DirectDebitSignatureSectionProps {
    onSignatureChange: (dataUrl: string | undefined) => void;
    onSubmit: () => void;
    isProcessing: boolean;
    hideButton?: boolean;
    disabled?: boolean;
    error?: string;
    buttonLabel?: string;
}

export const DirectDebitSignatureSection: React.FC<
    DirectDebitSignatureSectionProps
> = ({
    onSignatureChange,
    onSubmit,
    isProcessing,
    hideButton = false,
    disabled = false,
    error,
    buttonLabel = 'Continue'
}) => {
    const { isEnabled } = useFeatureFlags();
    const { t } = useI18n();
    const containerRef = useRef<HTMLDivElement>(null);
    const containerWidth = useContainerWidth(containerRef);
    const signatureWidth =
        Math.min(containerWidth, SIGNATURE_MAX_WIDTH) || SIGNATURE_MAX_WIDTH;
    const signatureMode = isEnabled('enableSignatureTypeInput')
        ? 'both'
        : 'draw';

    const [signatureData, setSignatureData] = useState<
        SignatureFieldData | undefined
    >(undefined);

    const hasSignature = Boolean(signatureData?.dataUrl);

    const handleSignatureChange = useCallback(
        (value?: SignatureFieldData) => {
            setSignatureData(value);
            onSignatureChange(value?.dataUrl);
        },
        [onSignatureChange]
    );

    const handleSubmit = useCallback(() => {
        if (!isProcessing && hasSignature && !disabled) {
            onSubmit();
        }
    }, [isProcessing, hasSignature, disabled, onSubmit]);

    return (
        <Box
            ref={containerRef}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}
        >
            <Signature
                width={signatureWidth}
                height={358}
                clearLabel={t('upc.signature.clear', 'Clear')}
                drawTabLabel={t('upc.signature.drawTab', 'Draw')}
                typeTabLabel={t('upc.signature.typeTab', 'Type')}
                typeInfoText={t(
                    'upc.signature.typeInfo',
                    'Type your full name to sign'
                )}
                textFieldLabel={t(
                    'upc.form.label.signature',
                    'Digital Signature'
                )}
                textFieldPlaceholder={t(
                    'upc.form.placeholder.signature',
                    'Type your full name to sign'
                )}
                mode={signatureMode}
                value={signatureData}
                onChange={handleSignatureChange}
                disabled={isProcessing}
                error={error}
                required
            />
            {!hideButton && (
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isProcessing || !hasSignature || disabled}
                    fullWidth
                    onClick={handleSubmit}
                    endIcon={
                        !isProcessing && <IconChevronRight color="inherit" />
                    }
                >
                    {isProcessing ? 'Processing...' : buttonLabel}
                </Button>
            )}
        </Box>
    );
};
